import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Container,
  Typography,
  CircularProgress,
  Alert,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Button,
  TableSortLabel,
  Card,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TablePagination,
  Autocomplete,
} from '@mui/material';
import { API_ENDPOINTS } from '../config/api';
import { SaveAlt as ExportIcon } from '@mui/icons-material';
import dayjs from 'dayjs';
import { useAppSettings } from '../context/AppSettingsContext';

function formatDeliveryDate(order) {
  if (!order) return 'N/A';
  if (order.delivery_date === 'Within' && order.delivery_date_days)
    return `Within (${order.delivery_date_days} days)`;
  if (order.delivery_date === 'Immediate' && order.delivery_date_hours)
    return `Immediate (${order.delivery_date_hours} hours)`;
  return order.delivery_date || 'N/A';
}

function convertQuotationsToCSV(quotations) {
  const header = [
    'S.No', 'App User', 'Buyer', 'Payment Terms', 'Delivery Terms',
    'Delivery Conditions', 'Delivery Date', 'Bending Charges',
    'Transport Charges', 'GST Amount', 'Total Amount', 'Status',
  ];
  const rows = quotations.map((q, idx) => [
    idx + 1,
    q.app_user?.company_name ?? '',
    q.customer_order?.app_user?.company_name ?? '',
    q.customer_order?.payment_terms ?? '',
    q.customer_order?.delivery_terms ?? '',
    q.customer_order?.delivery_conditions ?? '',
    formatDeliveryDate(q.customer_order),
    q.bending_charges ?? '',
    q.transport_charges ?? '',
    q.gst_amount ?? '',
    q.total_amount ?? '',
    q.status ?? '',
  ]);
  return [header, ...rows]
    .map((row) => row.map((field) => `"${String(field).replace(/"/g, '""')}"`).join(','))
    .join('\n');
}

const DealerQuotationReport = () => {
  const { deliveryConditions, deliveryDate } = useAppSettings();
  const [quotations, setQuotations] = useState([]);
  const [filteredQuotations, setFilteredQuotations] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: 'id', direction: 'asc' });
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [selectedPaymentTerm, setSelectedPaymentTerm] = useState('');
  const [selectedDeliveryTerm, setSelectedDeliveryTerm] = useState('');
  const [selectedDeliveryCondition, setSelectedDeliveryCondition] = useState('');
  const [selectedDeliveryDate, setSelectedDeliveryDate] = useState('');
  const [paymentTerms, setPaymentTerms] = useState([]);
  const [deliveryTerms, setDeliveryTerms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [exportSuccess, setExportSuccess] = useState(false);
  const [appUsers, setAppUsers] = useState([]);
  const [dealers, setDealers] = useState([]);
  const [selectedDealer, setSelectedDealer] = useState(null);
  const [selectedBuyer, setSelectedBuyer] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState('');
  

  // Transform delivery conditions string into array of objects
  const deliveryConditionsOptions = React.useMemo(() => {
    if (!deliveryConditions) return [];
    return deliveryConditions
      .split(",")
      .map((condition, index) => ({
        id: `${index + 1}`,
        name: condition.trim(),
      }))
      .filter((condition) => condition.name);
  }, [deliveryConditions]);

  // Transform delivery date string into array of objects
  const deliveryDateOptions = React.useMemo(() => {
    if (!deliveryDate) return [];
    return deliveryDate
      .split(",")
      .map((condition, index) => ({
        id: `${index + 1}`,
        name: condition.trim(),
      }))
      .filter((condition) => condition.name);
  }, [deliveryDate]);

  // Get unique status options from quotations
  const statusOptions = React.useMemo(() => {
    const statuses = quotations.map(q => q.status).filter(Boolean);
    return Array.from(new Set(statuses));
  }, [quotations]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [quotationsResponse, paymentTermsResponse, deliveryTermsResponse, appUsersResponse, dealersResponse] = await Promise.all([
          axios.get(API_ENDPOINTS.DEALER_QUOTATIONS.BASE),
          axios.get(API_ENDPOINTS.PAYMENT_TERMS.BASE),
          axios.get(API_ENDPOINTS.DELIVERY_TERMS.BASE),
          axios.get(API_ENDPOINTS.APP_USERS.BASE),
          axios.get(API_ENDPOINTS.DEALER_NAMES),
        ]);

        let data = quotationsResponse.data;
        const quotationsList = Array.isArray(data)
          ? data
          : Array.isArray(data.quotations)
          ? data.quotations
          : [];
        setQuotations(quotationsList);
        setFilteredQuotations(quotationsList);

        const paymentTermsData = Array.isArray(paymentTermsResponse.data.paymentTerms)
          ? paymentTermsResponse.data.paymentTerms
          : Array.isArray(paymentTermsResponse.data)
          ? paymentTermsResponse.data
          : paymentTermsResponse.data.data || [];
        setPaymentTerms(paymentTermsData);

        const deliveryTermsData = Array.isArray(deliveryTermsResponse.data.deliveryTerms)
          ? deliveryTermsResponse.data.deliveryTerms
          : Array.isArray(deliveryTermsResponse.data)
          ? deliveryTermsResponse.data
          : deliveryTermsResponse.data.data || [];
        setDeliveryTerms(deliveryTermsData);

        // Get unique buyers from quotations
        const uniqueBuyers = Array.from(new Set(quotationsList.map(q => q.customer_order?.app_user?.id)))
          .filter(id => id) // Remove any null/undefined values
          .map(id => {
            const quotation = quotationsList.find(q => q.customer_order?.app_user?.id === id);
            return quotation.customer_order.app_user;
          })
          .filter(buyer => buyer); // Remove any null/undefined values

        setAppUsers(uniqueBuyers);

        const dealerData = Array.isArray(dealersResponse.data.dealers)
          ? dealersResponse.data.dealers
          : Array.isArray(dealersResponse.data)
          ? dealersResponse.data
          : [];
        setDealers(dealerData);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to fetch data.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    let filtered = quotations;
    // Date filter
    if (fromDate) {
      filtered = filtered.filter((q) => {
        const date = q.created_at || q.quotation_date;
        return date && dayjs(date).isAfter(dayjs(fromDate).subtract(1, 'day'));
      });
    }
    if (toDate) {
      filtered = filtered.filter((q) => {
        const date = q.created_at || q.quotation_date;
        return date && dayjs(date).isBefore(dayjs(toDate).add(1, 'day'));
      });
    }
    // Payment terms filter
    if (selectedPaymentTerm) {
      filtered = filtered.filter((q) => 
        q.customer_order?.payment_terms === selectedPaymentTerm
      );
    }
    // Delivery terms filter
    if (selectedDeliveryTerm) {
      filtered = filtered.filter((q) => 
        q.customer_order?.delivery_terms === selectedDeliveryTerm
      );
    }
    // Delivery conditions filter
    if (selectedDeliveryCondition) {
      filtered = filtered.filter((q) => 
        q.customer_order?.delivery_conditions === selectedDeliveryCondition
      );
    }
    // Delivery date filter
    if (selectedDeliveryDate) {
      filtered = filtered.filter((q) => 
        q.customer_order?.delivery_date === selectedDeliveryDate
      );
    }
    // Dealer filter
    if (selectedDealer) {
      filtered = filtered.filter((q) => q.app_user?.id === selectedDealer.id);
    }
    // Buyer filter
    if (selectedBuyer) {
      filtered = filtered.filter((q) => q.customer_order?.app_user?.id === selectedBuyer.id);
    }
    // Status filter
    if (selectedStatus) {
      filtered = filtered.filter((q) => q.status === selectedStatus);
    }
    setFilteredQuotations(filtered);
  }, [quotations, fromDate, toDate, selectedPaymentTerm, selectedDeliveryTerm, selectedDeliveryCondition, selectedDeliveryDate, selectedDealer, selectedBuyer, selectedStatus]);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });

    const sorted = [...filteredQuotations].sort((a, b) => {
      const aVal = extractValue(a, key);
      const bVal = extractValue(b, key);
      return direction === 'asc'
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal));
    });

    setFilteredQuotations(sorted);
  };

  const extractValue = (quotation, key) => {
    switch (key) {
      case 'app_user': return quotation.app_user?.company_name ?? '';
      case 'buyer': return quotation.customer_order?.app_user?.company_name ?? '';
      case 'payment_terms': return quotation.customer_order?.payment_terms ?? '';
      case 'delivery_terms': return quotation.customer_order?.delivery_terms ?? '';
      case 'delivery_conditions': return quotation.customer_order?.delivery_conditions ?? '';
      case 'delivery_date': return formatDeliveryDate(quotation.customer_order);
      case 'bending_charges': return quotation.bending_charges ?? '';
      case 'transport_charges': return quotation.transport_charges ?? '';
      case 'gst_amount': return quotation.gst_amount ?? '';
      case 'total_amount': return quotation.total_amount ?? '';
      case 'status': return quotation.status ?? '';
      default: return quotation[key] ?? '';
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleExportCSV = () => {
    try {
      const paginatedQuotations =
        rowsPerPage > 0
          ? filteredQuotations.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
          : filteredQuotations;
      const csv = convertQuotationsToCSV(paginatedQuotations);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `dealer_quotations_report_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setExportSuccess(true);
      setTimeout(() => setExportSuccess(false), 3000);
    } catch (error) {
      setError('Failed to export data. Please try again.');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={10}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box mt={4}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box component="section" sx={{ mt: 4, mb: 4 }}>
      <Card sx={{ padding: 3, boxShadow: 3, borderRadius: 2 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {exportSuccess && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Successfully exported {rowsPerPage > 0 ? Math.min(rowsPerPage, filteredQuotations.length - page * rowsPerPage) : filteredQuotations.length} records to CSV
          </Alert>
        )}
        
        <Box display="flex" flexWrap="wrap" gap={2} alignItems="center" m={3}>
          <TextField
            label="From Date"
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 150 }}
          />
          <TextField
            label="To Date"
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 150 }}
          />
          <Autocomplete
            options={dealers}
            getOptionLabel={(option) => option.company_name || option.contact_person || `Dealer ${option.id}`}
            value={selectedDealer}
            onChange={(event, newValue) => {
              setSelectedDealer(newValue);
            }}
            sx={{ minWidth: 200 }}
            renderInput={(params) => <TextField {...params} label="Select Dealer" />}
          />
          <Autocomplete
            options={appUsers}
            getOptionLabel={(option) => option.company_name || option.contact_person || `User ${option.id}`}
            value={selectedBuyer}
            onChange={(event, newValue) => {
              setSelectedBuyer(newValue);
            }}
            sx={{ minWidth: 200 }}
            renderInput={(params) => <TextField {...params} label="Select Buyer" />}
          />
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Payment Terms</InputLabel>
            <Select
              value={selectedPaymentTerm}
              onChange={(e) => setSelectedPaymentTerm(e.target.value)}
              label="Payment Terms"
            >
              <MenuItem value="">All</MenuItem>
              {paymentTerms.map((term) => (
                <MenuItem key={term.id} value={term.name}>
                  {term.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Delivery Terms</InputLabel>
            <Select
              value={selectedDeliveryTerm}
              onChange={(e) => setSelectedDeliveryTerm(e.target.value)}
              label="Delivery Terms"
            >
              <MenuItem value="">All</MenuItem>
              {deliveryTerms.map((term) => (
                <MenuItem key={term.id} value={term.name}>
                  {term.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Delivery Conditions</InputLabel>
            <Select
              value={selectedDeliveryCondition}
              onChange={(e) => setSelectedDeliveryCondition(e.target.value)}
              label="Delivery Conditions"
            >
              <MenuItem value="">All</MenuItem>
              {deliveryConditionsOptions.map((condition) => (
                <MenuItem key={condition.id} value={condition.name}>
                  {condition.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Delivery Date</InputLabel>
            <Select
              value={selectedDeliveryDate}
              onChange={(e) => setSelectedDeliveryDate(e.target.value)}
              label="Delivery Date"
            >
              <MenuItem value="">All</MenuItem>
              {deliveryDateOptions.map((option) => (
                <MenuItem key={option.id} value={option.name}>
                  {option.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              label="Status"
            >
              <MenuItem value="">All</MenuItem>
              {statusOptions.map((status) => (
                <MenuItem key={status} value={status}>{status}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {(fromDate || toDate || selectedPaymentTerm || selectedDeliveryTerm || selectedDeliveryCondition || selectedDeliveryDate || selectedDealer || selectedBuyer || selectedStatus) && (
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => {
                setFromDate('');
                setToDate('');
                setSelectedPaymentTerm('');
                setSelectedDeliveryTerm('');
                setSelectedDeliveryCondition('');
                setSelectedDeliveryDate('');
                setSelectedDealer(null);
                setSelectedBuyer(null);
                setSelectedStatus('');
              }}
            >
              Clear Filters
            </Button>
          )}
          <Box sx={{ ml: 'auto' }}>
            <Button
              variant="contained"
              startIcon={<ExportIcon />}
              onClick={handleExportCSV}
              disabled={filteredQuotations.length === 0}
            >
              Export CSV ({rowsPerPage > 0 ? Math.min(rowsPerPage, filteredQuotations.length - page * rowsPerPage) : filteredQuotations.length} records)
            </Button>
          </Box>
        </Box>

        {filteredQuotations.length === 0 ? (
          <Alert severity="info">No dealer quotations found.</Alert>
        ) : (
          <>
            
            <TableContainer className="custom-table" component={Paper} sx={{ maxHeight: 600 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#1976d2' }}>
                    {[
                      ['S.No', 'sno'],
                      ['Dealer', 'app_user'],
                      ['Buyer', 'buyer'],
                      ['Payment Terms', 'payment_terms'],
                      ['Delivery Terms', 'delivery_terms'],
                      ['Delivery Conditions', 'delivery_conditions'],
                      ['Delivery Date', 'delivery_date'],
                      ['Bending Charges', 'bending_charges'],
                      ['Transport Charges', 'transport_charges'],
                      ['GST Amount', 'gst_amount'],
                      ['Total Amount', 'total_amount'],
                      ['Status', 'status'],
                    ].map(([label, key]) => (
                      <TableCell key={key} sx={{
                        backgroundColor: '#1976d2',
                        color: '#fff',
                        fontWeight: 'bold',
                      }}>
                        <TableSortLabel
                          active={sortConfig.key === key}
                          direction={sortConfig.key === key ? sortConfig.direction : 'asc'}
                          onClick={() => key !== 'sno' && handleSort(key)}
                          sx={{ color: 'inherit' }}
                          hideSortIcon={key === 'sno'}
                        >
                          {label}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredQuotations
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((q, idx) => (
                      <TableRow key={q.id} hover>
                        <TableCell>{page * rowsPerPage + idx + 1}</TableCell>
                        <TableCell>{q.app_user?.company_name || 'N/A'}</TableCell>
                        <TableCell>{q.customer_order?.app_user?.company_name || 'N/A'}</TableCell>
                        <TableCell>{q.customer_order?.payment_terms || 'N/A'}</TableCell>
                        <TableCell>{q.customer_order?.delivery_terms || 'N/A'}</TableCell>
                        <TableCell>{q.customer_order?.delivery_conditions || 'N/A'}</TableCell>
                        <TableCell>{formatDeliveryDate(q.customer_order)}</TableCell>
                        <TableCell>{q.bending_charges ?? 'N/A'}</TableCell>
                        <TableCell>{q.transport_charges ?? 'N/A'}</TableCell>
                        <TableCell>{q.gst_amount ?? 'N/A'}</TableCell>
                        <TableCell>{q.total_amount ?? 'N/A'}</TableCell>
                        <TableCell>{q.status || 'N/A'}</TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, { label: "All", value: -1 }]}
              colSpan={12}
              count={filteredQuotations.length}
              rowsPerPage={rowsPerPage}
              page={page}
              slotProps={{
                select: {
                  inputProps: {
                    "aria-label": "rows per page",
                  },
                  native: true,
                },
              }}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{
                border: "none",
                '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                  margin: 0,
                },
              }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, px: 2 }}>
              
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                Total Records: {filteredQuotations.length}
              </Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                Total Amount on Page: ₹ {filteredQuotations.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).reduce((sum, q) => sum + (q.total_amount || 0), 0).toFixed(2)}
              </Typography>
            </Box>
          </>
        )}
      </Card>
    </Box>
  );
};

export default DealerQuotationReport;
